import {CheckoutItem, removePromoCode, selectStoreId} from "../../redux/checkout/checkoutSlice";
import React from "react";
import Skeleton from "react-loading-skeleton";
import {Button} from "../buttons/Button";
import {
  AmexColorIcon,
  CouponIcon,
  DebitCardIcon,
  DinersClubColorIcon,
  DiscoverColorIcon,
  JcbColorIcon,
  MarkerIcon,
  MasterCardColorIcon,
  PhoneIcon,
  UnionPayColorIcon,
  VisaColorIcon
} from "../icons";
import {Body, Heading3, LocalizedTextLabel, Paragraph} from "../text";
import {CardBrand, CardModel} from "../../models/card";
import {ShoppingCartItemModel} from "../../models/shoppingCartItem";
import {useTranslation} from "next-i18next";
import {useModalRouting} from "../../utils/useModalRouting";
import {useRouter} from "next/router";
import {useAppDispatch, useAppSelector} from "../../app/hooks";

interface CheckoutItemViewProps {
  item: CheckoutItem
  isLoading: boolean
}

export const CheckoutItemView: React.FC<CheckoutItemViewProps> = ({ item, isLoading }) => {
  const { t } = useTranslation()
  const router = useRouter()
  const dispatch = useAppDispatch()
  const { createRoute } = useModalRouting()
  const storeId = useAppSelector(selectStoreId)

  const isCompleted = item.isCompleted

  const onItemClick = () => {
    if (isLoading) {
      return
    } else if (item.type === 'delivery_location') {
      router.push(
        createRoute({ name: 'location_entry' }),
        undefined,
        { shallow: true }
      );
    } else if (item.type === 'pickup_location') {

    } else if (item.type === 'contact') {
      router.push(
        createRoute({ name: 'contact_info_entry', query: { checkout: 'true' } }),
        undefined,
        { shallow: true }
      );
    } else if (item.type === 'card' && !item.value) {
      router.push(
        createRoute({ name: 'payment_entry' }),
        undefined,
        { shallow: true }
      );
    } else if (item.type === 'card' && item.value) {
      router.push(
        createRoute({ name: 'payment_selector' }),
        undefined,
        { shallow: true }
      );
    } else if (item.type === 'promo_code') {
      router.push(
        createRoute({ name: 'promo_code_entry', query: { store_id: storeId }  }),
        undefined,
        { shallow: true }
      );
    } else if (item.type === 'cart_item' && item.value instanceof ShoppingCartItemModel) {
      const productId = item.value.productId
      if (!productId) return

      router.push(
        createRoute({ name: 'product_detail', query: { product_id: productId } }),
        undefined,
        { shallow: true }
      );
    }
  }

  return <div
    className={`flex flex-row items-center space-x-4 lg:space-x-6 py-5 cursor-pointer border-b last:border-0 border-fg-gray-surface`}
    onClick={() => onItemClick()}
  >
    <div className="flex-shrink-0">
      {
        (() => {
          if (isLoading) {
            return <Skeleton className="!rounded-full !h-7 !w-7 !mx-1.5" />
          } else if (item.type === 'delivery_location' || item.type === 'pickup_location') {
            return <MarkerIcon className={`${isCompleted ? 'text-fg-black' : 'text-fg-primary'} h-8 w-8 mx-1`} />
          } else if (item.type === 'contact') {
            return <PhoneIcon className={`${isCompleted ? 'text-fg-black' : 'text-fg-primary'} h-8 w-8 mx-1`} />
          } else if (item.type === 'card') {
            const cardBrand: CardBrand | undefined = item.value instanceof CardModel ? item.value.brand : undefined

            return <div>
              <VisaColorIcon className={`h-8 w-8 mx-1 ${cardBrand === 'visa' || cardBrand === 'Visa' ? '' : 'hidden'}`} />
              <MasterCardColorIcon className={`h-8 w-8 mx-1 ${cardBrand === 'mastercard' || cardBrand === 'MasterCard' ? '' : 'hidden'}`} />
              <AmexColorIcon className={`h-8 w-8 mx-1 ${cardBrand === 'amex' || cardBrand === 'American Express' ? '' : 'hidden'}`} />
              <DiscoverColorIcon className={`h-8 w-8 mx-1 ${cardBrand === 'discover' || cardBrand === 'Discover' ? '' : 'hidden'}`} />
              <DinersClubColorIcon className={`h-8 w-8 mx-1 ${cardBrand === 'diners' || cardBrand === 'Diners Club' ? '' : 'hidden'}`} />
              <JcbColorIcon className={`h-8 w-8 mx-1 ${cardBrand === 'jcb' || cardBrand === 'JCB' ? '' : 'hidden'}`} />
              <UnionPayColorIcon className={`h-8 w-8 mx-1 ${cardBrand === 'union' || cardBrand === 'Union Pay' || cardBrand === 'Union Pay' ? '' : 'hidden'}`} />
              <DebitCardIcon className={`h-8 w-8 mx-1 ${cardBrand === 'unknown' || !cardBrand ? '' : 'hidden'} ${isCompleted ? 'text-fg-black' : 'text-fg-primary'}`} />
            </div>
          } else if (item.type === 'promo_code') {
            return <CouponIcon className="text-fg-black h-8 w-8 mx-1" />
          } else if (item.type === 'cart_item' && item.value instanceof ShoppingCartItemModel) {
            return <div className="bg-fg-gray-surface flex justify-center items-center rounded-full h-10 w-10">
              <Heading3>{item.value.quantity}x</Heading3>
            </div>
          }
        })()
      }
    </div>
    <div className="flex-grow flex flex-col space-y-1">
      <Body className={`${isCompleted ? '' : 'text-fg-primary'}`}>
        {isLoading ? <Skeleton className="!w-3/4"/> : <LocalizedTextLabel t={t} text={item.title}/>}
      </Body>
      {
        (() => {
          if (item.value instanceof ShoppingCartItemModel) {
            const optionDescription = item.value.optionValues
              .filter(o => o.value !== 'Default Title')
              .map(o => o.value)
              .join(', ')

            return optionDescription && <Paragraph className={`text-fg-gray-dark`}>
              {isLoading ? <Skeleton className="!w-1/3"/> : optionDescription}
            </Paragraph>
          }
        })()
      }
      <Paragraph className={`${item.subtitle ? 'block' : 'hidden'} text-fg-gray-dark`}>
        {isLoading ? <Skeleton className="!w-1/3"/> : <LocalizedTextLabel t={t} text={item.subtitle}/>}
      </Paragraph>
    </div>
    {item.buttonTitle && <div className="flex-shrink-0">
      <Button isSkeletonLoading={isLoading} buttonStyle={isCompleted ? 'secondary' : 'primary'} onClick={e => {
        e.stopPropagation()
        if (item.type === 'promo_code' && item.value) {
          dispatch(removePromoCode())
        } else {
          onItemClick()
        }
      }}>
        <LocalizedTextLabel t={t} text={item.buttonTitle} />
      </Button>
    </div>}
  </div>
}